import React from "react";
import { isEqual } from "lodash";

import { TableCell, TableRow, Checkbox } from "@mui/material";

import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from "react-beautiful-dnd";

import EnhancedTableCell from "./EnhancedTableCell";
import type { LazyTableRow, TableCellType } from "./index";

import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import MinusIcon from "@mui/icons-material/Remove";

/**
 * This is extandable table.
 */
type EnhancedTableRowProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLTableRowElement>,
  HTMLTableRowElement
> & {
  row: LazyTableRow;
  index: number;
  hover?: boolean;
  cells: Array<TableCellType>;
  withCheckbox?: boolean;
  handleClick: (event: React.MouseEvent, id: string, row: LazyTableRow) => void;
  isSelected: (id: string) => boolean;
  innerRef: (element?: HTMLElement | null) => any;
  draggableProps: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | undefined;
  collapsable: boolean;
};

const EnhancedTableRow = ({
  row,
  index,
  hover,
  cells,
  withCheckbox,
  handleClick,
  isSelected,
  innerRef,
  draggableProps,
  dragHandleProps,
  collapsable,
}: EnhancedTableRowProps) => {
  const labelId = `table-checkbox-${row.id}`;
  const isItemSelected = isSelected(row.id);

  const Row = (props: { row: any }) => {
    const [open, setOpen] = React.useState(false);
    const [expand, setExpand] = React.useState(false);

    /**
     *
     * @param row
     * Passing a row here for futher updates.
     */
    const handleExpandClick = (row: LazyTableRow) => {
      if (!open) {
        //alert(row.id);
      }
    };

    return (
      <React.Fragment>
        <TableRow
          hover={hover}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          selected={isItemSelected}
          ref={innerRef}
          {...draggableProps}
          {...dragHandleProps}
          data-cy={`${index}GridLink`}
          style={{ backgroundColor: row.color! }}
        >
          {collapsable && (
            <TableCell sx={{ width: withCheckbox ? "80px" : "auto", textAlign: "center" }}>
              {row.isExpandable && (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  sx={{ marginRight: withCheckbox ? "5px" : "auto" }}
                  onClick={() => setOpen(!open)}
                  onMouseEnter={() => setExpand(true)}
                  onMouseLeave={() => setExpand(false)}
                >
                  {open ? <MinusIcon /> : <AddIcon />}
                </IconButton>
              )}

              {withCheckbox && (
                <Checkbox
                  onClick={
                    !expand
                      ? ($e: any) => handleClick($e, row.id, row)
                      : () => handleExpandClick(row)
                  }
                  size="small"
                  checked={isItemSelected}
                  sx={{ padding: 0 }}
                  inputProps={{ "aria-labelledby": labelId }}
                />
              )}
            </TableCell>
          )}

          {cells.map((cell, i) => {
            const key = `${row.id}-${i}-${cell.accessor}`;
            return <EnhancedTableCell key={key} cell={cell} row={row} />;
          })}
        </TableRow>

        {open && (
          <TableRow>
            <TableCell />
            <TableCell colSpan={100}>
              {row.renderExpandedData && row.renderExpandedData()}
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return <Row key={row.id} row={row} />;
};

export default React.memo(EnhancedTableRow, isEqual);
